/**
 * Utility functions for making API calls to specific instances
 */
import { createApiClient } from '@shared/api-client'
import type { Project, Task } from '@shared/api-client'

export interface InstanceConfig {
  id: string
  name: string
  api_url: string
  status: string
  organization_domain: string
  admin_email: string
  created_at: string
  backend_port?: number
  postgres_port?: number
  postgres_db?: string
}

/**
 * Convert API response to InstanceConfig with camelCase properties
 */
export function normalizeInstanceConfig(apiInstance: any): InstanceConfig {
  return {
    id: apiInstance.id,
    name: apiInstance.name,
    api_url: apiInstance.api_url,
    status: apiInstance.status,
    organization_domain: apiInstance.organization_domain,
    admin_email: apiInstance.admin_email,
    created_at: apiInstance.created_at,
    backend_port: apiInstance.backend_port,
    postgres_port: apiInstance.postgres_port,
    postgres_db: apiInstance.postgres_db,
  }
}

/**
 * Create an API client for a specific instance
 */
export function createInstanceApiClient(instance: InstanceConfig) {
  return createApiClient({
    baseURL: instance.api_url,
    getToken: () => localStorage.getItem('token'),
    onUnauthorized: () => {
      // Don't logout on 401 from instance APIs - the admin token may not be valid for that instance
      // Just log the error - the calling code should handle it appropriately
      console.warn(`Authentication failed for instance ${instance.id}. Instance may not be ready or token not valid for this instance.`)
      // Don't redirect to login - let the component handle the error
    },
  })
}

/**
 * Get projects for a specific instance via proxy
 */
export async function getInstanceProjects(instance: InstanceConfig): Promise<Project[]> {
  // Use proxy endpoint instead of direct instance API
  const mainApiClient = createApiClient({
    baseURL: import.meta.env.VITE_API_URL || 'http://localhost:8000',
    getToken: () => localStorage.getItem('token'),
    onUnauthorized: () => {
      localStorage.removeItem('token')
      window.location.href = '/login'
    },
  })
  const response = await mainApiClient.get<Project[]>(`/api/v1/provisioning/${instance.id}/proxy/projects`)
  return response.data
}

/**
 * Get a project from a specific instance via proxy
 */
export async function getInstanceProject(instance: InstanceConfig, projectId: number): Promise<Project> {
  const mainApiClient = createApiClient({
    baseURL: import.meta.env.VITE_API_URL || 'http://localhost:8000',
    getToken: () => localStorage.getItem('token'),
    onUnauthorized: () => {
      localStorage.removeItem('token')
      window.location.href = '/login'
    },
  })
  const response = await mainApiClient.get<Project>(`/api/v1/provisioning/${instance.id}/proxy/projects/${projectId}`)
  return response.data
}

/**
 * Create a project in a specific instance via proxy
 */
export async function createInstanceProject(instance: InstanceConfig, projectData: any): Promise<Project> {
  const mainApiClient = createApiClient({
    baseURL: import.meta.env.VITE_API_URL || 'http://localhost:8000',
    getToken: () => localStorage.getItem('token'),
    onUnauthorized: () => {
      localStorage.removeItem('token')
      window.location.href = '/login'
    },
  })
  const response = await mainApiClient.post<Project>(`/api/v1/provisioning/${instance.id}/proxy/projects`, projectData)
  return response.data
}

/**
 * Get tasks for a specific instance via proxy
 */
export async function getInstanceTasks(instance: InstanceConfig, params?: any): Promise<Task[]> {
  const mainApiClient = createApiClient({
    baseURL: import.meta.env.VITE_API_URL || 'http://localhost:8000',
    getToken: () => localStorage.getItem('token'),
    onUnauthorized: () => {
      localStorage.removeItem('token')
      window.location.href = '/login'
    },
  })
  const response = await mainApiClient.get<Task[]>(`/api/v1/provisioning/${instance.id}/proxy/tasks`, { params })
  return response.data
}

/**
 * Create a task in a specific instance via proxy
 */
export async function createInstanceTask(instance: InstanceConfig, taskData: any): Promise<Task> {
  const mainApiClient = createApiClient({
    baseURL: import.meta.env.VITE_API_URL || 'http://localhost:8000',
    getToken: () => localStorage.getItem('token'),
    onUnauthorized: () => {
      localStorage.removeItem('token')
      window.location.href = '/login'
    },
  })
  const response = await mainApiClient.post<Task>(`/api/v1/provisioning/${instance.id}/proxy/tasks`, taskData)
  return response.data
}

/**
 * Create a user in a specific instance via proxy
 */
export async function createInstanceUser(instance: InstanceConfig, userData: any): Promise<any> {
  const mainApiClient = createApiClient({
    baseURL: import.meta.env.VITE_API_URL || 'http://localhost:8000',
    getToken: () => localStorage.getItem('token'),
    onUnauthorized: () => {
      localStorage.removeItem('token')
      window.location.href = '/login'
    },
  })
  const response = await mainApiClient.post(`/api/v1/provisioning/${instance.id}/proxy/users`, userData)
  return response.data
}

